import { useEffect, useLayoutEffect, useState } from 'react';
import './Contact.css';
import InputBox, { InputPolicy, InputType } from '../../components/InputBox/InputBox';
import TextSplit from '../../components/TextSplit/TextSplit';
import CheckBox from '../../components/CheckBox/CheckBox';
import axios from 'axios';
import { Box, FormControlLabel, FormGroup } from '@mui/material';
import Socials_slide from '../../components/socials/socials';
import { Helmet } from 'react-helmet';
import { BeatLoader } from 'react-spinners';

const Contact = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);

  const onClickAgreement = (checked: boolean) => {
    setIsAgreementChecked(checked);
  };

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const mediaElements = Array.from(document.querySelectorAll('img, video'));
    let loadedCount = 0;

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === mediaElements.length) {
        setIsLoading(false);
      }
    };

    if (mediaElements.length === 0) {
      setIsLoading(false);
    } else {
      mediaElements.forEach((el) => {
        if (el instanceof HTMLImageElement && el.complete) {
          checkAllLoaded();
        } else if (el instanceof HTMLVideoElement && el.readyState >= 3) {
          checkAllLoaded();
        } else {
          el.addEventListener('load', checkAllLoaded);
          el.addEventListener('error', checkAllLoaded);
        }
      });
    }

    return () => {
      mediaElements.forEach((el) => {
        el.removeEventListener('load', checkAllLoaded);
        el.removeEventListener('error', checkAllLoaded);
      });
    };
  }, []);

  useEffect(() => {
    document.title = 'Contact Us | Billet Precision Ltd.';
    document.documentElement.setAttribute('color-scheme', 'dark');
  }, []);

  useEffect(() => {
    if (isLoading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isLoading]);

  const handleSubmit = () => {
    let formData: { [key: string]: string } = {};
    let formIsValid = true;

    const contactForm = document.getElementById('contact-form') as HTMLDivElement;
    const contactFormInputElements = contactForm.getElementsByTagName('input');
    const contactFormTextAreaElements = contactForm.getElementsByTagName('textarea');

    for (let i = 0; i < contactFormInputElements.length; i++) {
      let currElement = contactFormInputElements[i] as HTMLInputElement;

      const currElementId = currElement.id;

      if (currElementId === 'company' && currElement.value.trim() === '') {
        continue;
      }

      if (currElement.value.trim() === '') {
        formIsValid = false;
      }

      if (currElement.id) {
        formData[currElement.id] = currElement.value;
      }
    }

    for (let i = 0; i < contactFormTextAreaElements.length; i++) {
      let currElement = contactFormTextAreaElements[i] as HTMLTextAreaElement;

      const currElementId = currElement.id;

      if (currElementId === 'message' && currElement.value.trim() === '') {
        continue;
      }

      formData[currElement.id] = currElement.value;
    }

    if (!isAgreementChecked) {
      formIsValid = false;
    }

    if (!formIsValid) {
      setSubmitMessage('Please fill in all required fields and agree to the terms.');
      return;
    }

    let formDataJson = JSON.stringify(formData);

    axios
      .post(`${process.env.REACT_APP_CONTACT_NOTIFICATION_BASE_URL}`, formDataJson, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        setSubmitMessage('Thank you! Your contact request has been sent.');
      })
      .catch((error) => {
        console.error('Error:', error);
        setSubmitMessage('An error occurred while sending your request. Please try again later.');
      });
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <BeatLoader
          color={'var(--accent-1--dark)'}
          loading={isLoading}
          size={150}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
      </div>
    );
  }

  return (
    <div className="main-content" id="contact">
      <Helmet>
        <title>Contact Us | Billet Precision Ltd.</title>
        <meta name="description" content="Get in touch with Billet Precision for consultations, quotes, and support." />
        <meta property="og:title" content="Contact Us | Billet Precision Ltd." />
        <meta property="og:description" content="Get in touch with Billet Precision for consultations, quotes, and support." />
        <link rel="canonical" href="https://billetprecision.ca/contact" />
      </Helmet>

      <div className="background-container" id="contact">
        <img className="background" id="contact" src="/img/contact/contact-bg.jpg" alt="background" />
        <div className="background-gradient-layer" id="contact"></div>
        <TextSplit
          className="main-content-text"
          id="contact"
          sx={{
            fontSize: {
              xs: '50px',
              sm: '80px',
              md: '110px',
              lg: '128px',
            },
          }}
        >
          Contact Us
        </TextSplit>
      </div>

      <div className="form-container" id="contact-form">
        <div className="form-metadata-container" id="contact-form">
          <TextSplit
            className="form-title"
            id="contact-form"
            sx={{
              fontSize: {
                xs: '24px',
                sm: '32px',
                md: '40px',
                lg: '48px',
              },
              fontWeight: 'bold',
            }}
          >
            Talk to our experts
          </TextSplit>

        </div>
        <div className="form" id="contact-form">
          <InputBox id="firstName" inputType={InputType.INPUT} placeholder="First Name" inputPolicy={InputPolicy.WORDS} />
          <InputBox id="lastName" inputType={InputType.INPUT} placeholder="Last Name" inputPolicy={InputPolicy.WORDS} />
          <InputBox id="email" inputType={InputType.INPUT} placeholder="Email" inputPolicy={InputPolicy.EMAIL} />
          <InputBox id="phoneNumber" inputType={InputType.INPUT} placeholder="Phone Number" inputPolicy={InputPolicy.PHONE_NUMBER} />
          <InputBox id="company" inputType={InputType.INPUT} placeholder="Company" inputPolicy={InputPolicy.WORDS} optional={true} />
          <InputBox id="industry" inputType={InputType.INPUT} placeholder="Industry" inputPolicy={InputPolicy.WORDS} />
          <InputBox id="message" inputType={InputType.TEXTAREA} placeholder="Your Message" inputPolicy={InputPolicy.WORDS} />
          <Box className="agreement-container">
            <a href="/legal/Billet-Precision-Terms-&-Conditions-Rev-C.pdf" target="_blank">Terms & Conditions</a>
            <FormGroup>
              <FormControlLabel control={<CheckBox id="agreement" onClick={onClickAgreement} />} label="I agree to terms and conditions*"></FormControlLabel>
            </FormGroup>
          </Box>
          <Box className="submit-button-container">
            <button type='submit' onClick={handleSubmit}>Submit</button>
          </Box>
        </div>
        {submitMessage && <div className="submit-message">{submitMessage}</div>}
      </div>

      <Box
        className="quote-request-email"
        sx={{
          fontSize: {
            xs: '14px',
            sm: '18px',
            md: '20px',
            lg: '24px',
          },
          textAlign: 'center',
        }}
      >
        For any other inquiries, please contact us at{' '}
        <a href="mailto:info@billetprecision.ca">info@billetprecision.ca</a>
      </Box>

      <Socials_slide />
    </div>
  );
};

export default Contact;
